import React, { useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import ENDPOINT from '../constants/endpoints';
import {MESSAGE_TYPE} from '../constants/message-types';
import POPUP_STYLES from '../constants/popup-styles';
import REASON from '../constants/reasons';
import ROUTE from '../constants/route';

import FileHelper from '../helpers/file';
import LinkBuilder from '../helpers/link-builder';
import {Permission, Role, Option} from '../helpers/user';
import useApi from '../hooks/useApi';
import BasicDialog from '../scenes/Dialog/components/BasicDialog';
import ConfirmDialog from '../scenes/Dialog/components/ConfirmDialog';
import FormDialog from '../scenes/Dialog/components/FormDialog';
import ResponseErrorDialog from '../scenes/Dialog/components/ResponseErrorDialog';
import UpgradeRecruiterAccount from '../scenes/Dialog/components/UpgradeRecruiterAccount';
import CandidateMassStatusForm from '../scenes/Form/components/CandidateMassStatusForm';
import MassSendJobForm from '../scenes/Form/components/MassSendJobForm';
import SendMessageForm from '../scenes/Form/components/SendMessageForm';
import ShortlistAddCandidatesForm from '../scenes/Form/components/ShortlistAddCandidatesForm';

const sendMessageAccessChecking = user => {
  if (!Permission.isAllowedSendMessageToCandidate(user.permissions)) {
    if (Role.isRecruiterDefault(user)) {
      UpgradeRecruiterAccount();
    } else {
      BasicDialog({
        bodyRender: 'You do not have permission to email candidates.',
        withoutButtons: true,
        bodyStyles: POPUP_STYLES.warningBodyStyles,
        popupStyles: POPUP_STYLES.warningPopupStyles,
        popupInnerStyles: POPUP_STYLES.warningContentStyles,
      });
    }
    return false;
  }
  return true;
};

const withSearchActions = WrappedComponent => props => {
  const suppProps = { ...props };
  const {
    user,
    globalOptions: { inboxRecipientsLimit, upgradeRequestEmail },
    globalActions: { loaderShow, loaderHide },
  } = props;

  const api = useApi();

  const [hasRecruiterCalendar] = useMemo(
    () => [Option.hasRecruiterCalendar(user.options)],
    [user]
  );

  const sendMessageForm = (title, { recipients, messageType }) => {
    FormDialog({
      title,
      modeless: false,
      compact: true,
      popupStyles: { padding: '0', maxWidth: '800px' },
      popupInnerStyles: { padding: '0 30px' },
      params: {
        recipients,
        messageType,
        upgradeRequestEmail,
        hasTemplateSelection: true,
      },
      form: (actions, params) => (
        <SendMessageForm actions={actions} params={params} />
      ),
    }).then(
      message =>
        BasicDialog({
          bodyRender: message,
          bodyStyles: POPUP_STYLES.warningBodyStyles,
          popupStyles: POPUP_STYLES.warningPopupStyles,
          popupInnerStyles: POPUP_STYLES.warningContentStyles,
          withoutButtons: true,
        }),
      response => ResponseErrorDialog({ response })
    );
  };

  const downloadExcel = (endpoint, id, candidates, filters = {}) => {
    const query = cloneDeep(filters);
    Object.keys(query).forEach(key => {
      if (
        query[key] === null ||
        typeof query[key] === 'undefined' ||
        !query[key].length
      ) {
        delete query[key];
      }
    });

    if (!candidates.length) {
      BasicDialog({
        bodyRender: 'Please select at least one candidate',
        bodyStyles: POPUP_STYLES.warningBodyStyles,
        popupStyles: POPUP_STYLES.warningPopupStyles,
        popupInnerStyles: POPUP_STYLES.warningContentStyles,
        withoutButtons: true,
      });
    } else {
      const params = { candidates: candidates.join(','), ...query };
      if (id) {
        params.id = id;
      }
      loaderShow();
      api.download(endpoint, params, {
        handleSuccess: response => {
          FileHelper.downloadInit(response.file, response.filename);
        },
        handleForbidden: response => {
          if (
            response.data &&
            response.data.data &&
            response.data.data.reason &&
            response.data.data.reason ===
              REASON.INSUFFICIENT_RECRUITER_COMPANY_LEVEL
          ) {
            UpgradeRecruiterAccount();
          } else {
            BasicDialog({
              bodyRender: 'You do not have permission to download resumes',
              bodyStyles: POPUP_STYLES.warningBodyStyles,
              popupStyles: POPUP_STYLES.warningPopupStyles,
              popupInnerStyles: POPUP_STYLES.warningContentStyles,
            });
          }
        },
        handleError: response => ResponseErrorDialog({ response }),
        handleComplete: () => loaderHide(),
      });
    }
  };

  const downloadResume = (endpoint, candidates, filters = {}, id = null) => {
    const query = cloneDeep(filters);
    Object.keys(query).forEach(key => {
      if (
        query[key] === null ||
        typeof query[key] === 'undefined' ||
        !query[key].length
      ) {
        delete query[key];
      }
    });

    if (!candidates.length) {
      BasicDialog({
        bodyRender: 'Please select at least one candidate',
        bodyStyles: POPUP_STYLES.warningBodyStyles,
        popupStyles: POPUP_STYLES.warningPopupStyles,
        popupInnerStyles: POPUP_STYLES.warningContentStyles,
        withoutButtons: true,
      });
    } else {
      const params = { candidates: candidates.join(','), ...query };
      if (id) {
        params.id = id;
      }
      loaderShow();
      api.download(endpoint, params, {
        handleSuccess: response => {
          FileHelper.downloadInit(response.file, response.filename);
        },
        handleForbidden: response => {
          if (
            response.data &&
            response.data.data &&
            response.data.data.reason &&
            response.data.data.reason ===
              REASON.INSUFFICIENT_RECRUITER_COMPANY_LEVEL
          ) {
            UpgradeRecruiterAccount();
          } else {
            BasicDialog({
              bodyRender: 'You do not have permission to download resumes',
              bodyStyles: POPUP_STYLES.warningBodyStyles,
              popupStyles: POPUP_STYLES.warningPopupStyles,
              popupInnerStyles: POPUP_STYLES.warningContentStyles,
            });
          }
        },
        handleError: response => ResponseErrorDialog({ response }),
        handleComplete: () => loaderHide(),
      });
    }
  };

  suppProps.searchAction = {
    addToShortlist(ids, withoutLimit = false) {
      const limit = 500;
      let warning = null;
      if (ids.length < 1) {
        warning =
          'Please check at least one candidate to add this candidate to Shortlist';
      } else if (!withoutLimit && ids.length > limit) {
        warning = `You are allowed to add not more than ${limit} candidates at once.`;
      }

      if (warning) {
        BasicDialog({
          bodyRender: warning,
          bodyStyles: POPUP_STYLES.warningBodyStyles,
          popupStyles: POPUP_STYLES.warningPopupStyles,
          popupInnerStyles: POPUP_STYLES.warningContentStyles,
          withoutButtons: true,
        });
      } else {
        FormDialog({
          title: 'Add to Shortlist',
          popupStyles: {
            maxWidth: '550px',
          },
          compact: true,
          modeless: false,
          params: { ids },
          form: (actions, params) => (
            <ShortlistAddCandidatesForm actions={actions} params={params} />
          ),
        }).then(
          ({ id: shortListId }) => {
            ConfirmDialog({
              bodyRender: 'Selected candidates have been added to Shortlist',
              textCancelBtn: 'Stay here',
              textSuccessBtn: 'Go to Shortlist',
              dismissWhenClose: true,
            }).then(() => {
              props.history.push(LinkBuilder.getShortlistLink(shortListId));
            });
          },
          response => ResponseErrorDialog({ response })
        );
      }
    },

    removeFromShortlist(shortlistId, ids) {
      return new Promise(resolve => {
        if (ids.length < 1) {
          BasicDialog({
            bodyRender:
              'Please select at least one candidate to remove him from Shortlist',
            bodyStyles: POPUP_STYLES.warningBodyStyles,
            popupStyles: POPUP_STYLES.warningPopupStyles,
            popupInnerStyles: POPUP_STYLES.warningContentStyles,
            withoutButtons: true,
          });
        } else {
          ConfirmDialog({
            bodyRender:
              'Are you sure you want to remove selected candidates from Shortlist?',
            textSuccessBtn: 'Yes',
            textCancelBtn: 'No',
            dismissWhenClose: true,
          }).then(() =>
            api.post(
              ENDPOINT.ACTION.SHORTLIST.DELETE_CANDIDATES,
              {
                id: shortlistId,
                candidates: ids,
              },
              {
                handleError: response => ResponseErrorDialog(response),
                handleSuccess: () => resolve(),
              }
            )
          );
        }
      });
    },

    message(recipients, to, messageType) {
      if (sendMessageAccessChecking(user) && recipients) {
        sendMessageForm(`Message to ${to}`, { recipients, messageType });
      }
    },

    messageSelected(recipients, messageType) {
      if (sendMessageAccessChecking(user)) {
        let warning = null;
        if (!recipients.length) {
          warning = 'Please select at least one candidate';
        } else if (
          Role.isRecruiterBasic(user) &&
          recipients.length > inboxRecipientsLimit &&
          messageType === MESSAGE_TYPE.TYPE_RECRUITER_SEARCH
        ) {
          warning = `Maximum ${inboxRecipientsLimit} messages at once allowed.`;
        }

        if (warning) {
          BasicDialog({
            bodyRender: warning,
            bodyStyles: POPUP_STYLES.warningBodyStyles,
            popupStyles: POPUP_STYLES.warningPopupStyles,
            popupInnerStyles: POPUP_STYLES.warningContentStyles,
            withoutButtons: true,
          });
        } else {
          sendMessageForm('Email All Selected Candidates', {
            recipients,
            messageType,
          });
        }
      }
    },

    massSendJob(ids) {
      if (!ids.length) {
        BasicDialog({
          bodyRender: 'Please select at least one candidate',
          bodyStyles: POPUP_STYLES.warningBodyStyles,
          popupStyles: POPUP_STYLES.warningPopupStyles,
          popupInnerStyles: POPUP_STYLES.warningContentStyles,
          withoutButtons: true,
        });
      } else if (ids.length > 500) {
        BasicDialog({
          bodyRender: 'You cannot select more than 500 candidates at once',
          bodyStyles: POPUP_STYLES.warningBodyStyles,
          popupStyles: POPUP_STYLES.warningPopupStyles,
          popupInnerStyles: POPUP_STYLES.warningContentStyles,
          withoutButtons: true,
        });
      } else {
        FormDialog({
          title: 'Send Job',
          compact: true,
          modeless: false,
          params: { ids, isSendJobButton: true, successBtnText: 'Send Job' },
          form: (actions, params) => (
            <MassSendJobForm actions={actions} params={params} />
          ),
        }).then(
          () => {
            BasicDialog({
              bodyRender: 'Messages have been sent successfully.',
            });
          },
          response => {
            ResponseErrorDialog({ response });
          }
        );
      }
    },

    sendLinksEmail(id, ids) {
      if (!ids.length) {
        BasicDialog({
          bodyRender:
            'Please check at least one candidate to add this candidate to Links email',
          bodyStyles: POPUP_STYLES.warningBodyStyles,
          popupStyles: POPUP_STYLES.warningPopupStyles,
          popupInnerStyles: POPUP_STYLES.warningContentStyles,
          withoutButtons: true,
        });
      } else {
        loaderShow();
        api.post(
          ENDPOINT.ACTION.SEND_LINKS_EMAIL,
          { id, candidates: ids },
          {
            handleSuccess: () =>
              BasicDialog({
                bodyRender: 'Email with links generated and sent to your email',
              }),
            handleError: response => ResponseErrorDialog({ response }),
            handleComplete: () => loaderHide(),
          }
        );
      }
    },

    sendGatEmail(id, ids) {
      if (!ids.length) {
        BasicDialog({
          bodyRender:
            'To administer the GoBuyside Aptitude Test, please select at least 1 applicant.',
          bodyStyles: POPUP_STYLES.warningBodyStyles,
          popupStyles: POPUP_STYLES.warningPopupStyles,
          popupInnerStyles: POPUP_STYLES.warningContentStyles,
          withoutButtons: true,
        });
      } else {
        ConfirmDialog({
          bodyRender:
            'Would you like to administer the GoBuyside Aptitude Test to selected applicants?',
          textSuccessBtn: 'Yes',
          textCancelBtn: 'No, Thanks',
          dismissWhenClose: true,
        }).then(() => {
          loaderShow();
          api.post(
            ENDPOINT.ACTION.SEND_GAT_EMAIL,
            { id, candidates: ids },
            {
              handleSuccess: () =>
                BasicDialog({
                  bodyRender:
                    'Request for completing GoBuyside Aptitude Test has been sent to selected applicants.',
                }),
              handleError: response => ResponseErrorDialog({ response }),
              handleComplete: () => loaderHide(),
            }
          );
        });
      }
    },

    sendScheduleEmail(id, ids) {
      if (!hasRecruiterCalendar) {
        ConfirmDialog({
          bodyRender:
            'To send an Appoint Meeting email you need to activate your calendar.\n' +
            'Do you want to open the page with calendar settings?',
          popupStyles: POPUP_STYLES.warningPopupStyles,
          textSuccessBtn: 'Yes',
          textCancelBtn: 'Cancel',
          dismissWhenClose: true,
        }).then(() => {
          props.history.push(ROUTE.RECRUITER_MANAGE_CALENDAR);
        });
      } else if (!ids.length) {
        BasicDialog({
          bodyRender:
            'Please check at least one candidate to send Appoint meeting email',
          bodyStyles: POPUP_STYLES.warningBodyStyles,
          popupStyles: POPUP_STYLES.warningPopupStyles,
          popupInnerStyles: POPUP_STYLES.warningContentStyles,
          withoutButtons: true,
        });
      } else {
        ConfirmDialog({
          bodyRender: `Are you sure you want to send Appoint meeting email to selected candidates?`,
          popupStyles: POPUP_STYLES.warningPopupStyles,
          textSuccessBtn: 'Yes',
          textCancelBtn: 'No',
          dismissWhenClose: true,
        }).then(() => {
          loaderShow();
          api.post(
            ENDPOINT.ACTION.SEND_SCHEDULE_EMAIL,
            { id, candidates: ids },
            {
              handleSuccess: () =>
                BasicDialog({
                  bodyRender:
                    'Appoint Meeting email has been sent to selected applicants',
                }),
              handleError: response => ResponseErrorDialog({ response }),
              handleComplete: () => loaderHide(),
            }
          );
        });
      }
    },

    downloadResumeApplicants(id, candidates, filters) {
      downloadResume(
        ENDPOINT.ACTION.CANDIDATE_DOWNLOAD_RESUME.APPLICANTS,
        candidates,
        filters,
        id
      );
    },

    downloadResumeShortlist(id, candidates, filters) {
      downloadResume(
        ENDPOINT.ACTION.CANDIDATE_DOWNLOAD_RESUME.SHORTLIST,
        candidates,
        filters,
        id
      );
    },

    downloadResumeRated(candidates, filters) {
      downloadResume(
        ENDPOINT.ACTION.CANDIDATE_DOWNLOAD_RESUME.RATED,
        candidates,
        filters
      );
    },

    downloadResumeInbounds(candidates, filters) {
      downloadResume(
        ENDPOINT.ACTION.CANDIDATE_DOWNLOAD_RESUME.INBOUNDS,
        candidates,
        filters
      );
    },

    downloadExcelApplicants(id, candidates, filters) {
      downloadExcel(
        ENDPOINT.ACTION.CANDIDATE_DOWNLOAD_EXCEL.APPLICANTS,
        id,
        candidates,
        filters
      );
    },

    downloadExcelShortlist(id, candidates, filters) {
      downloadExcel(
        ENDPOINT.ACTION.CANDIDATE_DOWNLOAD_EXCEL.SHORTLIST,
        id,
        candidates,
        filters
      );
    },

    downloadExcelRated(candidates, filters) {
      downloadExcel(
        ENDPOINT.ACTION.CANDIDATE_DOWNLOAD_EXCEL.RATED,
        undefined,
        candidates,
        filters
      );
    },

    setMassStatus(id, ids) {
      return new Promise((resolve, reject) => {
        if (!ids.length) {
          BasicDialog({
            bodyRender: 'Please check at least one candidate',
            bodyStyles: POPUP_STYLES.warningBodyStyles,
            popupStyles: POPUP_STYLES.warningPopupStyles,
            popupInnerStyles: POPUP_STYLES.warningContentStyles,
            withoutButtons: true,
          });
        } else if (ids.length > 50) {
          BasicDialog({
            bodyRender: 'You cannot change status for more than 50 candidates',
            bodyStyles: POPUP_STYLES.warningBodyStyles,
            popupStyles: POPUP_STYLES.warningPopupStyles,
            popupInnerStyles: POPUP_STYLES.warningContentStyles,
            withoutButtons: true,
          });
        } else {
          FormDialog({
            title: 'Set status to candidates',
            compact: true,
            popupStyles: POPUP_STYLES.formPopupStyles,
            popupInnerStyles: POPUP_STYLES.formInnerStyles,
            popupTitleStyles: POPUP_STYLES.formTitleStyles,
            params: { id, ids },
            form: (actions, params) => (
              <CandidateMassStatusForm actions={actions} params={params} />
            ),
          }).then(
            () => resolve(),
            response => {
              ResponseErrorDialog({ response });
              reject();
            }
          );
        }
      });
    },
  };

  return <WrappedComponent {...suppProps} />;
};

export default withSearchActions;
